import { formatDate } from '@angular/common';
import { IBlockTimeItem } from 'src/app/ReusableComponent/block-time-report/block-time-report.model';
import { BlockTimeBillingFormGroup, BlockTimeHoursFormGroup } from '../types/block-time-record-form.type';
import { calculateRateAndFlightMargin } from './calculate-rate-and-flight-margin.helper';
import { toNullableNumber } from './to-nullable-number.helper';

/**
 * Builds the save body of the legacy record save endpoint. The function does not change the draft.
 * The hours value is null in edit mode, because the user cannot change the hours there.
 * The rate and the flight margin are calculated again only if the user changed the cost or the profit,
 * as in the legacy modal. Otherwise the stored values are saved.
 * In add mode the service request date is sent as 'yyyy-MM-dd', as the legacy add form sent it.
 */
export function buildBlockTimeRecordPayload(
  draft: IBlockTimeItem,
  billing: ReturnType<BlockTimeBillingFormGroup['getRawValue']>,
  hours: ReturnType<BlockTimeHoursFormGroup['getRawValue']> | null,
  isRateChanged: boolean,
): IBlockTimeItem {
  const { rate, flightMargin } = isRateChanged
    ? calculateRateAndFlightMargin(billing.cost, billing.profit)
    : { rate: draft.rate, flightMargin: draft.flightMargin };
  const hourFields = hours
    ? {
        srCreatedOnDate: draft.srCreatedOnDate ? formatDate(draft.srCreatedOnDate, 'yyyy-MM-dd', 'en-US') : null,
        outboundReposition: hours.outboundReposition?.id ?? null,
        inboundReposition: hours.inboundReposition?.id ?? null,
        routing: hours.routing?.trim() || null,
        obEstimatedBlockHours: Number(hours.obHours),
        liveFlightHours: Number(hours.liveFlightHours),
        ibEstimatedBlockHours: Number(hours.ibHours),
        cancellationHours: Number(hours.cancellationHours),
        estimatedBlockHours: hours.estimatedBlockHours,
      }
    : {
        obEstimatedBlockHours: Number(draft.obEstimatedBlockHours),
        liveFlightHours: Number(draft.liveFlightHours),
        ibEstimatedBlockHours: Number(draft.ibEstimatedBlockHours),
        cancellationHours: Number(draft.cancellationHours),
      };

  return {
    ...draft,
    ...hourFields,
    totalBilledHours: toNullableNumber(billing.totalBilledHours),
    cost: billing.cost,
    profit: billing.profit,
    rate,
    flightMargin,
    invoiceID: billing.invoiceID,
    monthYear: billing.monthYear?.id ?? '',
    ar: billing.ar,
    ap: billing.ap,
    note: billing.note,
  };
}
